import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    Flame, Snowflake, Thermometer, CloudRain, AlertTriangle,
    Wind as WindIcon, LayoutGrid, Compass, Droplets,
} from "lucide-react";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Legend, LabelList, Cell,
} from "recharts";
import { fetchFarm, fetchThresholds, fetchFrostHistory, fetchYtdRainfall } from "@/lib/api";
import { stationColor } from "@/lib/stationColors";
import {
    fdiLevel, deltaTLevel, frostLevel, LEVEL_LABELS, RISK_COLORS,
    fmt, levelRank, sprayBadge,
} from "@/lib/risk";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StatusBadge from "@/components/StatusBadge";
import CombinedWeatherCard from "@/components/CombinedWeatherCard";
import HfdiTrendCard from "@/components/HfdiTrendCard";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Column & Filter registry – filters change the columns shown in the matrix.
// ---------------------------------------------------------------------------
const CONDITION_EMOJI = { Sunny: "☀", "Partly Cloudy": "⛅", Clear: "☾", Overcast: "☁", Rain: "🌧" };

function fmtHM(m) {
    if (!m) return null;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    if (h === 0) return `${rem}m`;
    if (rem === 0) return `${h}h`;
    return `${h}h${rem}m`;
}

function frostSeverity(minTemp, threshold) {
    if (minTemp === undefined || minTemp === null) return "green";
    if (minTemp <= threshold - 2) return "red";
    if (minTemp <= threshold) return "orange";
    if (minTemp <= threshold + 1.5) return "yellow";
    return "green";
}

const COLUMNS = {
    temp: {
        label: "Temp",
        width: "w-16",
        render: ({ s }) => <span>{fmt(s.current.temp, 1)}°</span>,
        nav: (s) => `/stations/${s.id}`,
    },
    humidity: {
        label: "RH",
        width: "w-14",
        render: ({ s }) => <span>{fmt(s.current.humidity, 0)}%</span>,
        nav: (s) => `/stations/${s.id}`,
    },
    wind: {
        label: "Wind",
        width: "w-24",
        render: ({ s }) => (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    <Compass className="h-2.5 w-2.5" />{s.current.wind_dir}
                </span>
                <span>{fmt(s.current.wind, 1)}</span>
            </span>
        ),
        nav: (s) => `/stations/${s.id}`,
    },
    gust: {
        label: "Gust",
        width: "w-14",
        render: ({ s }) => <span>{fmt(s.current.gust, 1)}</span>,
        nav: (s) => `/stations/${s.id}`,
    },
    fdi: {
        label: "HFDI",
        width: "w-16",
        chip: ({ s, thr }) => ({ level: fdiLevel(s.current.fdi, thr.fdi), text: fmt(s.current.fdi, 0) }),
        onClick: (s, ctx) => ctx.openRank("fdi"),
        headerAction: (ctx) => ctx.openRank("fdi"),
    },
    delta_t: {
        label: "ΔT",
        width: "w-20",
        chip: ({ s, thr }) => ({ level: deltaTLevel(s.current.delta_t, thr.delta_t), text: `${fmt(s.current.delta_t, 1)}°` }),
        onClick: (s, ctx) => ctx.openRank("delta_t"),
        headerAction: (ctx) => ctx.openRank("delta_t"),
    },
    spray: {
        label: "Spray",
        width: "w-24",
        chip: ({ s }) => {
            const b = sprayBadge(s.spray);
            return { level: b.level, text: b.label };
        },
        nav: () => "/spray",
    },
    frost: {
        label: "Frost",
        width: "w-32",
        chip: ({ s, thr, frostEvent, frostThreshold }) => {
            if (frostEvent) {
                return {
                    level: frostSeverity(frostEvent.min_temp, frostThreshold),
                    text: <span>{fmtHM(frostEvent.minutes_below)} <span className="opacity-70 font-normal">· {fmt(frostEvent.min_temp, 1)}°</span></span>,
                };
            }
            return {
                level: frostLevel(s.forecast_min, thr.frost),
                text: `${fmt(s.forecast_min, 1)}°`,
            };
        },
        nav: () => "/frost-history",
    },
    frost_mins: {
        label: "< 2.2°C",
        width: "w-20",
        render: ({ frostEvent }) => (
            <span>{frostEvent ? fmtHM(frostEvent.minutes_below) : <span className="text-slate-400">—</span>}</span>
        ),
        nav: () => "/frost-history",
    },
    min_temp: {
        label: "Min",
        width: "w-20",
        render: ({ s, frostEvent }) => {
            const minT = frostEvent ? frostEvent.min_temp : s.min_temp;
            return <span>{fmt(minT, 1)}°</span>;
        },
        nav: (s) => `/stations/${s.id}`,
    },
    rain_24h: {
        label: "Rain 24h",
        width: "w-20",
        render: ({ s }) => <span>{fmt(s.rain_24h, 1)}<span className="text-slate-400 font-normal"> mm</span></span>,
        nav: () => "/rainfall",
    },
    rain_7d: {
        label: "Rain 7d",
        width: "w-20",
        render: ({ s }) => <span>{fmt(s.rain_7d, 1)}<span className="text-slate-400 font-normal"> mm</span></span>,
        nav: () => "/rainfall",
    },
    max_min: {
        label: "Max/Min",
        width: "w-20",
        render: ({ s }) => <span>{fmt(s.max_temp, 0)}/{fmt(s.min_temp, 0)}°</span>,
        nav: (s) => `/stations/${s.id}`,
    },
};

const FILTERS = [
    { key: "all",   label: "All",   icon: LayoutGrid,
      cols: ["temp", "wind", "gust", "fdi", "delta_t", "frost", "rain_24h", "rain_7d", "max_min"] },
    { key: "spray", label: "Spray", icon: WindIcon,
      cols: ["temp", "humidity", "wind", "gust", "delta_t", "spray"] },
    { key: "frost", label: "Frost", icon: Snowflake,
      cols: ["temp", "min_temp", "frost", "frost_mins"] },
    { key: "fire",  label: "Fire",  icon: Flame,
      cols: ["fdi", "temp", "humidity", "wind", "gust", "rain_24h"] },
];

function currentDateTime() {
    return new Date().toLocaleString("en-AU", {
        weekday: "long", day: "numeric", month: "long",
        hour: "2-digit", minute: "2-digit", hour12: false,
    });
}

export default function HomePage() {
    const { data, isLoading } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm, refetchInterval: 60_000 });
    const { data: thr } = useQuery({ queryKey: ["thresholds"], queryFn: fetchThresholds });
    const { data: frostHistory } = useQuery({
        queryKey: ["frost-history", 3],
        queryFn: () => fetchFrostHistory(3),
        refetchInterval: 5 * 60_000,
    });
    const [rankMetric, setRankMetric] = useState(null);

    if (isLoading || !data || !thr) return <LoadingHome />;

    const { farm, stations, alerts, condition, farm_name } = data;
    const activeFilter = FILTERS[0];
    const cols = activeFilter.cols;

    const latestFrost = {};
    if (frostHistory) {
        Object.entries(frostHistory.nights).forEach(([sid, entry]) => {
            const events = [...entry.events].sort((a, b) => b.date.localeCompare(a.date));
            latestFrost[sid] = events[0] || null;
        });
    }
    const frostThreshold = frostHistory?.threshold_c ?? thr.frost_threshold_c ?? 2.2;
    const ctx = { openRank: setRankMetric };

    return (
        <div className="space-y-4" data-testid="home-page">
            {/* LIVE WEATHER SNAPSHOT MATRIX */}
            <div className="fw-card overflow-hidden" data-testid="live-snapshot">
                <div className="px-3 sm:px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Live Weather Snapshot</div>
                        <div className="text-[11px] text-slate-400 fw-metric">{stations.length} stations · updated {new Date(data.generated_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <span className="hidden sm:inline text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                        tap any cell
                    </span>
                </div>

                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-[13px]" data-testid="snapshot-table">
                        <thead>
                            <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/60 border-b border-slate-100">
                                <th className="px-3 py-2 sticky left-0 bg-slate-50/95 backdrop-blur z-10">Station</th>
                                {cols.map((c) => (
                                    <ColumnHeader key={c} colKey={c} col={COLUMNS[c]} ctx={ctx} />
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {stations.map((s) => (
                                <SnapshotRow
                                    key={s.id} s={s} thr={thr}
                                    cols={cols}
                                    ctx={ctx}
                                    frostEvent={latestFrost[s.id]}
                                    frostThreshold={frostThreshold}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Portrait mobile — dedicated station cards, no horizontal scroll */}
                <div className="md:hidden divide-y divide-slate-100">
                    {stations.map((s) => (
                        <MobileStationCard
                            key={s.id} s={s} thr={thr}
                            ctx={ctx}
                            frostEvent={latestFrost[s.id]}
                            frostThreshold={frostThreshold}
                        />
                    ))}
                </div>
            </div>

            <CombinedWeatherCard />

            <HfdiTrendCard />

            <YtdRainfallCard />

            {/* Secondary: compact compare charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TempCompareCard stations={stations} />
                <RainCompareCard stations={stations} />
            </div>

            <AlertsPanel alerts={alerts} />

            <StationRankModal
                open={rankMetric !== null}
                onClose={() => setRankMetric(null)}
                metric={rankMetric}
                stations={stations}
                thr={thr}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Table primitives
// ---------------------------------------------------------------------------
function ColumnHeader({ colKey, col, ctx }) {
    const cls = "px-2 py-2 whitespace-nowrap text-right hover:text-slate-900 transition-colors";
    if (col.headerAction) {
        return (
            <th className="px-2 py-2 text-right whitespace-nowrap">
                <button className="hover:text-slate-900" onClick={() => col.headerAction(ctx)}
                    data-testid={`snapshot-head-${colKey}`}>
                    {col.label}
                </button>
            </th>
        );
    }
    if (col.nav) {
        return (
            <th className="px-2 py-2 text-right whitespace-nowrap">
                <Link className="hover:text-slate-900" to={col.nav({ id: "" })}
                    data-testid={`snapshot-head-${colKey}`}>
                    {col.label}
                </Link>
            </th>
        );
    }
    return <th className={cls} data-testid={`snapshot-head-${colKey}`}>{col.label}</th>;
}

function SnapshotRow({ s, thr, cols, ctx, frostEvent, frostThreshold }) {
    const nav = (to) => () => window.location.assign(to);
    return (
        <tr
            data-testid={`snapshot-row-${s.id}`}
            className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors cursor-pointer"
            onClick={nav(`/stations/${s.id}`)}
        >
            <td className="px-3 py-2 sticky left-0 bg-white/95 backdrop-blur z-10 whitespace-nowrap">
                <Link
                    to={`/stations/${s.id}`}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`snapshot-station-${s.id}`}
                    className="font-bold text-slate-900 text-sm hover:text-[var(--fw-accent)]"
                >
                    {s.name}
                </Link>
                <div className="text-[10px] text-slate-500 leading-none mt-0.5">{s.condition}</div>
            </td>
            {cols.map((c) => (
                <MatrixCell key={c} colKey={c} col={COLUMNS[c]} s={s} thr={thr}
                    ctx={ctx} frostEvent={frostEvent} frostThreshold={frostThreshold} />
            ))}
        </tr>
    );
}

// Portrait-mobile compact station row — control-panel style.
// Fixed 3-line dense layout showing every core metric, so all 4 stations
// fit on one phone screen with no horizontal or vertical scroll.
function MobilePill({ level, label, text, testid, onClick }) {
    const c = RISK_COLORS[level] || RISK_COLORS.green;
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border fw-metric text-[11px] font-bold leading-none whitespace-nowrap active:brightness-95"
            style={{ background: c.bg, borderColor: c.border, color: c.text }}
        >
            <span className="text-[9px] font-bold uppercase tracking-wider opacity-70">{label}</span>
            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: c.hex }} />
            <span>{text}</span>
        </button>
    );
}

function MobileStationCard({ s, thr, ctx, frostEvent, frostThreshold }) {
    const nav = (to) => (e) => { e.stopPropagation(); window.location.assign(to); };
    const stationHref = `/stations/${s.id}`;

    const fdi = fdiLevel(s.current.fdi, thr.fdi);
    const dt = deltaTLevel(s.current.delta_t, thr.delta_t);
    let frostChip;
    if (frostEvent) {
        frostChip = {
            level: frostSeverity(frostEvent.min_temp, frostThreshold),
            text: `${fmt(frostEvent.min_temp, 1)}°`,
        };
    } else {
        frostChip = {
            level: frostLevel(s.forecast_min, thr.frost),
            text: `${fmt(s.forecast_min, 1)}°`,
        };
    }

    const tempVal = s.current?.temp;

    return (
        <div
            data-testid={`mobile-snapshot-${s.id}`}
            onClick={() => window.location.assign(stationHref)}
            className="px-3 py-2 hover:bg-slate-50 active:bg-slate-100 cursor-pointer"
        >
            {/* Row 1 — station name + current temp */}
            <div className="flex items-baseline justify-between gap-2">
                <Link
                    to={stationHref}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`mobile-snapshot-name-${s.id}`}
                    className="font-bold text-slate-900 text-[15px] leading-none uppercase tracking-wide truncate hover:text-[var(--fw-accent)]"
                    style={{ fontFamily: "Outfit" }}
                >
                    {s.name}
                </Link>
                <div className="fw-metric text-xl font-extrabold text-slate-900 leading-none flex-shrink-0">
                    {tempVal !== null && tempVal !== undefined
                        ? `${fmt(tempVal, 1)}°`
                        : <span className="text-slate-400">—</span>}
                </div>
            </div>

            {/* Row 2 — wind & gust + risk pills */}
            <div className="mt-1.5 flex items-center justify-between gap-2 text-[12px]">
                <button
                    onClick={nav(stationHref)}
                    data-testid={`mobile-wind-${s.id}`}
                    className="inline-flex items-center gap-1 fw-metric font-semibold text-slate-700 leading-none whitespace-nowrap"
                >
                    <Compass className="h-3 w-3 text-slate-400" />
                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">{s.current.wind_dir}</span>
                    <span>{fmt(s.current.wind, 1)}</span>
                    <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider ml-1">G</span>
                    <span>{fmt(s.current.gust, 1)}</span>
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <MobilePill
                        level={fdi} label="HFDI" text={fmt(s.current.fdi, 0)}
                        testid={`mobile-cell-fdi-${s.id}`}
                        onClick={(e) => { e.stopPropagation(); ctx.openRank("fdi"); }}
                    />
                    <MobilePill
                        level={dt} label="ΔT" text={`${fmt(s.current.delta_t, 1)}°`}
                        testid={`mobile-cell-delta_t-${s.id}`}
                        onClick={(e) => { e.stopPropagation(); ctx.openRank("delta_t"); }}
                    />
                    <MobilePill
                        level={frostChip.level} label="Frost" text={frostChip.text}
                        testid={`mobile-cell-frost-${s.id}`}
                        onClick={nav("/frost-history")}
                    />
                </div>
            </div>

            {/* Row 3 — rainfall & max/min */}
            <div className="mt-1.5 flex items-center justify-between gap-2 text-[12px] fw-metric font-semibold text-slate-700">
                <button
                    onClick={nav("/rainfall")}
                    data-testid={`mobile-cell-rain_24h-${s.id}`}
                    className="inline-flex items-center gap-1 whitespace-nowrap"
                >
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Rain 24h</span>
                    <span>{fmt(s.rain_24h, 1)}<span className="text-slate-400 font-normal">mm</span></span>
                </button>
                <button
                    onClick={nav("/rainfall")}
                    data-testid={`mobile-cell-rain_7d-${s.id}`}
                    className="inline-flex items-center gap-1 whitespace-nowrap"
                >
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">7d</span>
                    <span>{fmt(s.rain_7d, 1)}<span className="text-slate-400 font-normal">mm</span></span>
                </button>
                <button
                    onClick={nav(stationHref)}
                    data-testid={`mobile-cell-max_min-${s.id}`}
                    className="inline-flex items-center gap-1 whitespace-nowrap"
                >
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Max/Min</span>
                    <span>{fmt(s.max_temp, 0)}/{fmt(s.min_temp, 0)}°</span>
                </button>
            </div>
        </div>
    );
}


function MatrixCell({ colKey, col, s, thr, ctx, frostEvent, frostThreshold }) {
    const stop = (e) => e.stopPropagation();
    const handleClick = (e) => {
        stop(e);
        if (col.onClick) col.onClick(s, ctx);
        else if (col.nav) window.location.assign(col.nav(s));
    };

    if (col.chip) {
        const { level, text } = col.chip({ s, thr, frostEvent, frostThreshold });
        const c = RISK_COLORS[level] || RISK_COLORS.green;
        return (
            <td className="px-2 py-2 text-right whitespace-nowrap">
                <button
                    onClick={handleClick}
                    data-testid={`snapshot-cell-${colKey}-${s.id}`}
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md fw-metric font-semibold text-[12px] leading-tight border"
                    style={{ background: c.bg, borderColor: c.border, color: c.text }}
                >
                    <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: c.hex }} />
                    {text}
                </button>
            </td>
        );
    }
    return (
        <td className="px-2 py-2 text-right whitespace-nowrap">
            <button
                onClick={handleClick}
                data-testid={`snapshot-cell-${colKey}-${s.id}`}
                className="fw-metric font-semibold text-[12.5px] text-slate-800 hover:text-slate-900"
            >
                {col.render({ s, thr, frostEvent, frostThreshold })}
            </button>
        </td>
    );
}

// ---------------------------------------------------------------------------
// Secondary components (compare charts, alerts, rank modal)
// ---------------------------------------------------------------------------
function TempCompareCard({ stations }) {
    const data = stations.map((s) => ({ name: s.name, max: s.max_temp, min: s.min_temp }));
    return (
        <div className="fw-card p-4" data-testid="home-temp-compare">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Max / Min today</h2>
                    <p className="text-[11px] text-slate-500">°C per station</p>
                </div>
                <Thermometer className="h-4 w-4 text-slate-400" />
            </div>
            <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 10, right: 8, left: -22, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 600 }} />
                        <YAxis tick={{ fontSize: 10 }} unit="°" />
                        <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, fontSize: 12 }} />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="max" name="Max" fill="#DC2626" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="min" name="Min" fill="#2563EB" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

const RAIN_RANGES = [
    { key: "rain_24h", label: "24h" },
    { key: "rain_7d", label: "7d" },
    { key: "rain_30d", label: "30d" },
    { key: "rain_growing_season", label: "Growing" },
];

function RainCompareCard({ stations }) {
    const [range, setRange] = useState("rain_24h");
    const data = stations.map((s) => ({ name: s.name, value: s[range] }));
    const total = data.reduce((a, r) => a + r.value, 0);
    const barColour = (v) => (v >= 15 ? "#16A34A" : v >= 5 ? "#65A30D" : v > 0 ? "#CA8A04" : "#94A3B8");
    return (
        <div className="fw-card p-4" data-testid="home-rain-compare">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                    <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Rainfall by station</h2>
                    <p className="text-[11px] text-slate-500 fw-metric">Total {fmt(total, 1)} mm</p>
                </div>
                <Tabs value={range} onValueChange={setRange}>
                    <TabsList className="bg-slate-100 p-0.5 h-7">
                        {RAIN_RANGES.map((r) => (
                            <TabsTrigger key={r.key} value={r.key} className="text-[11px] px-2 h-6" data-testid={`home-rain-range-${r.label}`}>
                                {r.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            </div>
            <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 44, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fontWeight: 600 }} width={70} />
                        <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, fontSize: 12 }} formatter={(v) => [`${fmt(v, 1)} mm`, "Rain"]} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {data.map((d, i) => <Cell key={i} fill={barColour(d.value)} />)}
                            <LabelList dataKey="value" position="right" formatter={(v) => `${fmt(v, 1)}`} style={{ fill: "#0F172A", fontSize: 10, fontWeight: 700 }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

// YTD lines now use the canonical per-station palette from lib/stationColors
// (dropped in favour of stationColor(id, idx) below).

function YtdRainfallCard() {
    const { data } = useQuery({
        queryKey: ["ytd-rainfall"],
        queryFn: fetchYtdRainfall,
        refetchInterval: 5 * 60_000,
    });

    const rows = useMemo(() => {
        if (!data) return [];
        const map = new Map();
        data.stations.forEach((st) => {
            st.series.forEach((pt) => {
                if (!map.has(pt.date)) map.set(pt.date, { date: pt.date });
                map.get(pt.date)[st.name] = pt.cumulative;
            });
        });
        return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
    }, [data]);

    if (!data) return <div className="fw-card h-40 animate-pulse" data-testid="home-ytd-rainfall" />;

    const yearStart = data.year;
    const xFmt = (v) => new Date(v).toLocaleDateString("en-AU", { month: "short", day: "numeric" });

    return (
        <div className="fw-card p-4" data-testid="home-ytd-rainfall">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div>
                    <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>YTD cumulative rainfall</h2>
                    <p className="text-[11px] text-slate-500 fw-metric">1 Jan {yearStart} → today · mm</p>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {data.stations.map((s, i) => (
                        <Link
                            key={s.id}
                            to={`/stations/${s.id}`}
                            data-testid={`ytd-legend-${s.id}`}
                            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 hover:text-slate-900"
                        >
                            <span className="h-2 w-2 rounded-full" style={{ background: stationColor(s.id, i) }} />
                            {s.name}
                            <span className="fw-metric text-slate-500 font-normal">{fmt(s.total, 1)} mm</span>
                        </Link>
                    ))}
                </div>
            </div>
            <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rows} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tickFormatter={xFmt}
                            tick={{ fontSize: 10, fontWeight: 600, fill: "#334155" }}
                            minTickGap={44} tickMargin={4} />
                        <YAxis
                            tick={{ fontSize: 10, fontWeight: 600, fill: "#334155" }}
                            width={54} tickMargin={4}
                            allowDecimals={false}
                            tickFormatter={(v) => `${v} mm`}
                            domain={[0, (dataMax) => Math.max(10, Math.ceil((dataMax + 5) / 5) * 5)]}
                        />
                        <Tooltip
                            labelFormatter={xFmt}
                            contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, fontSize: 12 }}
                            formatter={(v, name) => [v === null || v === undefined ? "no data" : `${fmt(v, 1)} mm`, name]}
                        />
                        {data.stations.map((s, i) => (
                            <Line
                                key={s.id}
                                type="linear"
                                dataKey={s.name}
                                stroke={stationColor(s.id, i)}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}


function AlertsPanel({ alerts }) {
    if (alerts.length === 0) {
        return (
            <div className="fw-card p-3 flex items-center gap-2" data-testid="alerts-panel">
                <AlertTriangle className="h-4 w-4 text-emerald-500" />
                <span className="text-sm text-slate-600">No alerts right now — everything looks good.</span>
            </div>
        );
    }
    return (
        <div className="fw-card p-3" data-testid="alerts-panel">
            <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Recent alerts</h2>
                <span className="text-[10px] text-slate-400 fw-metric">{alerts.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {alerts.slice(0, 6).map((a, i) => {
                    const c = RISK_COLORS[a.severity] || RISK_COLORS.green;
                    return (
                        <div key={i} className="flex items-start gap-2 p-2 rounded-md border" style={{ background: c.bg, borderColor: c.border }} data-testid={`alert-${i}`}>
                            <div className="h-1.5 w-1.5 mt-1.5 rounded-full flex-shrink-0" style={{ background: c.hex }} />
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] font-semibold" style={{ color: c.text }}>
                                    {a.metric} · {a.station}
                                </div>
                                <div className="text-xs text-slate-800 font-medium leading-tight">{a.message}</div>
                                <div className="text-[10px] text-slate-500 fw-metric">{a.value}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function StationRankModal({ open, onClose, metric, stations, thr }) {
    const rows = useMemo(() => {
        if (!metric || !stations) return [];
        const items = stations.map((s) => {
            switch (metric) {
                case "fdi": return { s, sortVal: s.current.fdi, level: fdiLevel(s.current.fdi, thr.fdi), primary: `HFDI ${fmt(s.current.fdi, 0)}`, secondary: `${LEVEL_LABELS[fdiLevel(s.current.fdi, thr.fdi)]} · Temp ${fmt(s.current.temp, 1)}° · RH ${fmt(s.current.humidity, 0)}%` };
                case "delta_t": return { s, sortVal: -Math.abs(s.current.delta_t - 5) - (deltaTLevel(s.current.delta_t, thr.delta_t) === "green" ? 100 : 0), level: deltaTLevel(s.current.delta_t, thr.delta_t), primary: `${fmt(s.current.delta_t, 1)}°C`, secondary: `Wind ${fmt(s.current.wind, 1)} km/h · Gust ${fmt(s.current.gust, 1)}` };
                default: return null;
            }
        }).filter(Boolean);
        items.sort((a, b) => (levelRank(b.level) - levelRank(a.level)) || (b.sortVal - a.sortVal));
        return items;
    }, [metric, stations, thr]);

    const titleMap = { fdi: "Harvest Fire Danger Index — worst first", delta_t: "Delta T — most marginal first" };
    const heroTag = { fdi: "Highest HFDI", delta_t: "Worst spray window" };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg" data-testid="rank-modal">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: "Outfit" }}>{titleMap[metric]}</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 mt-2">
                    {rows.map(({ s, primary, secondary, level }, i) => (
                        <Link
                            key={s.id}
                            to={`/stations/${s.id}`}
                            onClick={onClose}
                            data-testid={`rank-row-${s.id}`}
                            className="flex items-center justify-between gap-3 p-4 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="h-3.5 w-3.5 rounded-full flex-shrink-0" style={{ background: RISK_COLORS[level].hex }} />
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-slate-900 truncate">{s.name}</span>
                                        {i === 0 && (
                                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md" style={{ background: RISK_COLORS[level].bg, color: RISK_COLORS[level].text, border: `1px solid ${RISK_COLORS[level].border}` }}>
                                                {heroTag[metric]}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate">{secondary}</div>
                                </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <div className="fw-metric font-bold text-slate-900">{primary}</div>
                                <StatusBadge level={level} size="sm" />
                            </div>
                        </Link>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function LoadingHome() {
    return (
        <div className="animate-pulse space-y-4">
            <div className="h-10 w-64 bg-slate-200 rounded-md" />
            <div className="h-48 bg-white border border-slate-200 rounded-2xl" />
        </div>
    );
}
